export type TMediavineHintSizeMode = 'auto' | 'manual';

export type TBuildMediavineHintSizesParams = {
  reservedWidth: number;
  reservedHeight: number;
  mode?: TMediavineHintSizeMode;
  manualSizes?: string;
};

const MOBILE_SAFE_WIDTH = 320;

const MEDIAVINE_SIZE_ALLOWLIST: Array<[number, number]> = [
  [300, 250],
  [728, 90],
  [300, 600],
  [160, 600],
  [120, 600],
  [320, 50],
  [300, 50],
  [320, 100],
  [250, 250],
  [468, 60],
  [336, 280],
  [970, 250],
  [970, 90],
  [300, 1050],
  [300, 200],
  [320, 480],
  [300, 400]
];

function formatSize([width, height]: [number, number]): string {
  return `${width}x${height}`;
}

export function buildMediavineHintSizes({
  reservedWidth,
  reservedHeight,
  mode = 'auto',
  manualSizes
}: TBuildMediavineHintSizesParams): string | undefined {
  if (mode === 'manual') {
    return manualSizes;
  }

  if (reservedWidth <= 0 || reservedHeight <= 0) {
    return undefined;
  }

  const fittingSizes = MEDIAVINE_SIZE_ALLOWLIST.filter(([width, height]) => {
    return width <= reservedWidth && height <= reservedHeight;
  }).sort((a, b) => {
    const areaDiff = b[0] * b[1] - a[0] * a[1];
    if (areaDiff !== 0) {
      return areaDiff;
    }

    const widthDiff = b[0] - a[0];
    if (widthDiff !== 0) {
      return widthDiff;
    }

    return b[1] - a[1];
  });

  if (fittingSizes.length === 0) {
    return undefined;
  }

  const hasTooWideSizes = fittingSizes.some(([width]) => width > MOBILE_SAFE_WIDTH);
  if (!hasTooWideSizes) {
    return fittingSizes.map(formatSize).join(',');
  }

  const mobileSubset = fittingSizes
    .filter(([width]) => width <= MOBILE_SAFE_WIDTH)
    .map((size) => `m${formatSize(size)}`);
  const remainingWideSizes = fittingSizes.filter(([width]) => width > MOBILE_SAFE_WIDTH).map(formatSize);

  return [...mobileSubset, ...remainingWideSizes].join(',');
}
